import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Pill, PillDot } from '@/lib/base/badges/pill';
import { pillClassName, pillTintStyle } from '@/lib/base/badges/pill-styles';
import { cx } from '@/utils/cx';

import { stopClickPropagation } from './stopClickPropagation';
import { useStageLinkData } from './useStageLinkData';

const DEFAULT_STAGE_COLOR = '#7A5AF8'; // utility-brand-500

/** The "current" side of a transition reads quieter than the proposed side. */
const MUTED_TEXT_CLASS = 'text-fg-tertiary';

/** Hover affordance when the badge deep-links to the pipeline board. */
const LINK_HOVER_CLASS = 'transition-opacity hover:opacity-80 cursor-pointer';

export interface PipelineStageValueBadgeProps {
  // Data
  /** Stage entity_id (UUID). */
  value: string | null | undefined;
  /** Optional org ID for stage lookup; falls back to OrgContext internally. */
  orgId?: number;
  /**
   * Fallback label to render when the CRM store hasn't loaded the stage yet.
   * Useful in contexts (e.g. Storybook) where there's no live backend.
   */
  fallbackLabel?: string;

  // Presentation
  /**
   * Size variant on the shared chip scale: 'sm' uses text-xs, 'md' (default)
   * uses text-sm — the same height and type as a `Badge` of that size.
   */
  size?: 'sm' | 'md';
  /**
   * Muted variant: renders with neutral colors (no stage color tint).
   * Use for "current" stage in transition UIs (current → proposed).
   */
  muted?: boolean;
  className?: string;

  // Behavior
  /**
   * When true (default) and the stage's pipeline can be resolved, the badge
   * renders as a link to the pipeline view with `?stage=<id>` — PipelineBoard
   * scrolls the matching column into view. Set false on surfaces where
   * navigating away is wrong (e.g. inside a modal flow like record merge).
   */
  interactive?: boolean;
}

/**
 * Color-coded stage badge — a pill tinted from the pipeline stage's own color
 * (background wash, ring, text and leading dot).
 *
 * This is the canonical "stage chip" for any surface that needs a visually
 * prominent stage indicator (pipeline columns, action cards, record headers).
 * For a plain-text stage label, use `PipelineStageValueLabel` instead.
 *
 * Rendered through the base `Pill`, so it sits on the same size scale as every
 * `Badge` — a stage chip beside an entity badge is the same height and type. It
 * previously carried its own bordered, semibold sizing, which made it taller
 * than every chip around it.
 *
 * @example
 * <PipelineStageValueBadge value="stage-entity-uuid" />
 * // Renders: [● In Progress] tinted with the stage's pipeline color
 */
export const PipelineStageValueBadge: React.FC<
  PipelineStageValueBadgeProps
> = ({
  value,
  orgId,
  fallbackLabel,
  size = 'md',
  muted = false,
  className = '',
  interactive = true,
}) => {
  // Deep link to the stage's pipeline view. Null while unresolved, in which
  // case the badge stays a plain (non-navigating) pill. `enabled: false`
  // when the badge can't or shouldn't navigate — non-interactive or no
  // value — so opt-out and `(not set)` badges never trigger the pipelines
  // fetch behind useStagePipelineRoute.
  const shouldResolveRoute = interactive && Boolean(value);
  const { stage, isLoading, pipelineRoute } = useStageLinkData(value, {
    orgId,
    enabled: shouldResolveRoute,
  });
  const stageColor = stage?.color || DEFAULT_STAGE_COLOR;
  const stageLabel = stage?.label || fallbackLabel;

  // Only the router Link applies the tint by hand — a <Pill> computes its own.
  const linkTintStyle = useMemo(
    () => (muted ? undefined : pillTintStyle(stageColor)),
    [muted, stageColor]
  );

  if (!value) {
    return (
      <span className={cx('text-sm text-fg-quaternary italic', className)}>
        (not set)
      </span>
    );
  }

  if (isLoading && !stageLabel) {
    return (
      <Pill size={size} className={cx('animate-pulse', className)}>
        ...
      </Pill>
    );
  }

  if (!stageLabel) {
    return (
      <span className={cx('text-sm text-fg-tertiary', className)}>Stage</span>
    );
  }

  if (pipelineRoute) {
    // A router Link can't be a <Pill> span, so it takes the Pill's classes,
    // tint and dot directly — same scale, no forked styles.
    return (
      <Link
        to={pipelineRoute}
        title={`Open ${stageLabel} in the pipeline`}
        onClick={stopClickPropagation}
        className={pillClassName({
          size,
          hasDot: !muted,
          tint: muted ? undefined : stageColor,
          className: cx(muted && MUTED_TEXT_CLASS, LINK_HOVER_CLASS, className),
        })}
        style={linkTintStyle}
      >
        {!muted && <PillDot dotColor={stageColor} />}
        {stageLabel}
      </Link>
    );
  }

  if (muted) {
    return (
      <Pill size={size} className={cx(MUTED_TEXT_CLASS, className)}>
        {stageLabel}
      </Pill>
    );
  }

  return (
    <Pill size={size} tint={stageColor} dot className={className}>
      {stageLabel}
    </Pill>
  );
};
